import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { useDispatch, useSelector } from "react-redux";
import { loadUser } from "./actions/userActions";
import ProtectedRoute from "./auth/ProtectedRoute";

// AUTH & ADMIN

const Logout = lazy(() => import("./pages/Logout"));
const LoginPage = lazy(() => import("./pages/Auth/LoginPage"));
const RegisterPage = lazy(() => import("./pages/Auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/Auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/Auth/ResetPasswordPage"));
const SelectFranchisePage = lazy(() => import("./pages/admin/SectionFranchise/select-franchise"));
const CreateFranchisePage = lazy(() => import("./pages/admin/CreateFranchise/CreateFranchisePage"));
const AdminConsole = lazy(() => import("./pages/admin/AdminConsole/AdminConsole"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const UserPage = lazy(() => import("./pages/userPage"));
// LAZY StockRoutes (entire nested stock route structure)
const StockRoutes = lazy(() => import("./routes/StockRoutes"));
const SaleRoutes = lazy(() => import("./routes/SaleRoutes"));
const BusinessInsightsRoutes = lazy(() => import("./routes/BusinessInsightsRoutes"));

const App = () => {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.user);

  useEffect(() => {
    if (!user) {
      dispatch(loadUser());
    }
  }, [dispatch, user]);

  // Role-based redirect for root "/"
  const roleBasedRedirect = () => {
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (user?.role === "admin") return <Navigate to="/franchise" replace />;
    if (user?.role === "sale") return <Navigate to="/sales" replace />;
    if (user?.role === "stock") return <Navigate to="/stock" replace />;
    if (user?.role === "profit-loss") return <Navigate to="/business-insights" replace />;
    return <Navigate to="/login" replace />;
  };

  return (
    <Router>
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          {/* Home redirect */}
          <Route path="/" element={roleBasedRedirect()} />

          {/* PUBLIC ROUTES */}
          <Route path="/login" element={<LoginPage />} />
           <Route path="/logout" element={<Logout />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

          {/* ADMIN/FRANCHISE PROTECTED ROUTES */}
          <Route
            path="/franchise"
            element={
              <ProtectedRoute role="admin">
                <SelectFranchisePage />
              </ProtectedRoute>
            }
          />
           <Route
            path="/user"
            element={
              <ProtectedRoute role="admin">
                <UserPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/franchise/create"
            element={
              <ProtectedRoute role="admin">
                <CreateFranchisePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/adminconsole"
            element={
              <ProtectedRoute role="admin">
                <AdminConsole />
              </ProtectedRoute>
            }
          />

          {/* NESTED STOCK ROUTES */}
          <Route
            path="/stock/*"
            element={
              <ProtectedRoute role={["stock", "admin"]}>
                <StockRoutes />
              </ProtectedRoute>
            }
          />
           <Route
            path="/sales/*"
            element={
              <ProtectedRoute role={["sale", "admin"]}>
                <SaleRoutes />
              </ProtectedRoute>
            }
          />
           <Route
            path="/business-insights/*"
            element={
              <ProtectedRoute role={["sale", "admin"]}>
                <BusinessInsightsRoutes />
              </ProtectedRoute>
            }
          />

          {/* 404 PAGE */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;
